import { TeamMembersIcon, UserInfoIcon } from '@icons/index.ts'

import { SearchIcon, PlusIcon,  InfoOutlineIcon, MinusIcon } from '@icons/utils'
import { useNotify, useUpdate, useDataProvider } from 'react-admin'

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import styles from './AccountUsers.module.scss'
import sharedStyles from '@src/pages/MultiPage.module.scss'
import { LxIcon } from '@components/icon/Icon.tsx'
import { cn } from '@src/utils/cn.ts'
import { useParams, useSearchParams } from 'react-router-dom'
import { UserF } from '@logic/contexts/Users/UsersFrontend.type.ts'
import { LxTooltip } from '@components/tooltip/tooltip.tsx'
import { mapRoleToReadableRole } from '@src/utils/mapRoleToReadableRole.ts'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import UserInfo from './UserInfo'
import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { usePolicyContext } from '@logic/contexts/AppStore/AbacContext.tsx'
import { LxAutocompleteTmp } from '@components/autocompleteTmp/autocompleteTmp.tsx'
import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { USER_STATE } from '@src/utils/constants'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { AccountSettings } from '@src/logic/contexts/Accounts/AccountsFrontend.type'
import { checkAccSettingsScopeWithSettings } from '@src/utils/accountSettings'
import { formatDate } from '@src/utils/dateFormatter'
import CreateUser from './CreateorEdit/CreateUser'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { CircularProgress } from '@mui/material'
import { mapStateToReadableStatus } from '@src/utils/mapStateToReadableState'
import { UserActions } from './UserActions/UserActions'
import { UserB } from '@src/logic/contexts/Users/UsersBackend.type'

const ACTIVE_USER_STATES = [USER_STATE.ACTIVE, USER_STATE.INVITED, USER_STATE.INVITE_DELIVERED, USER_STATE.INVITE_FAILED, USER_STATE.LOCKED]
const ADMIN_ROLES = ['account_admin', 'admin']

const getTooltipText = (state: string) => {
  switch (state) {
    case USER_STATE.INVITED:
      return 'The user is invited. Awaiting email delivery status.'
    case USER_STATE.INVITE_DELIVERED:
      return 'The invitation has been sent. The user status will change to Active once the user signs in.'
    case USER_STATE.INVITE_FAILED:
      return 'The invitation email failed to deliver. You can retry sending the email or delete the user.'
    default:
      return ''
  }
}

// We dont have inline spiiner anywhere in the app, so added customStyles for Loading Spinner here
const customStyles = {
  smallSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '40px',
    height: '24px',
  },
}

export const AccountUsers = () => {
  const { accountId } = useParams()
  const [searchParams] = useSearchParams()
  const accountName = searchParams.get('name') ?? ''

  const [isUserDataLoading, setIsUserDataLoading] = useState(false)
  const [isSettingDataLoading, setIsSettingDataLoading] = useState(false)
  const [settings, setSettings] = useState<Partial<AccountSettings>>({})
  const [userData, setUserData] = useState<[UserF] | null>(null)
  const [processing2FAUserId, setProcessing2FAUserId] = useState<string | null>(null)
  const { showModal } = useModal()

  const [stateFilter, setStateFilter] = useState<string>(USER_STATE.ACTIVE)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const { canAccess: canLockUnLockUser, effectiveValue } = checkAccSettingsScopeWithSettings('lock_unlock_users', settings)
  const { canAccess: canClearMFADevice, effectiveValue: userRolesWith2FA } = checkAccSettingsScopeWithSettings('multifactor_auth', settings)

  const { currentUser } = useUserContext() as any
  const { canPerformAction } = usePolicyContext()
  const canEditGlobalUser = canPerformAction(currentUser, { type: 'GlobalUser' }, 'create')
  const canEditUser = currentUser.accountId === accountId && canPerformAction(currentUser, { type: 'User' }, 'lock_unlock')

  const accHasLockUnLock = effectiveValue === 'true'
  const show2FAReset = canClearMFADevice && userRolesWith2FA !== 'disabled' && canEditGlobalUser
  const hasLockFunction = accHasLockUnLock && canLockUnLockUser && ( canEditGlobalUser ||  canEditUser)


  const dataProvider = useDataProvider()
  const notify = useNotify()
  const [update] = useUpdate('Report')

  const sanitizeData = (data: any) => {
    if (!data || !data.report) {
      return null
    }
    return data.report.map((user: UserB) => ({
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      fullName: `${user.first_name} ${user.last_name}`,
      primaryEmail: user.primary_email,
      state: user.state,
      lastLoginFormatted: user.last_login ? formatDate(user.last_login) : 'N/A',
      userRole: user.user_role,
      jobTitle: user.job_title,
    }))
  }

  const fetchData = useCallback(async () => {
    if (!accountId) return

    setIsUserDataLoading(true)
    try {
      await update(
        'Report',
        {
          id: accountId,
          data: { filter: {}, type: 'user_info' },
          previousData: null,
        },
        {
          onSuccess: (data) => {
            const sanitized = sanitizeData(data)
            if (sanitized) setUserData(sanitized)
            setIsUserDataLoading(false)
          },
          onError: (error) => {
            notify((error as { message?: string })?.message || 'Something went wrong', { type: 'warning' })
            setIsUserDataLoading(false)
          },
        }
      )
    } catch {
      setIsUserDataLoading(false)
    }
  }, [accountId, update, notify])

  // fetch acc settings
  const fetchAccSettings = useCallback(async () => {
    if (!accountId) return
    setIsSettingDataLoading(true)
    try {
      const response = await dataProvider.getOne('Account', {
        id: accountId,
        method: 'accountSettings',
      })
      const settingsData = response.data as AccountSettings
      const filteredSettings = Object.fromEntries(Object.entries(settingsData).filter(([key]) => !['id', 'account_id', 'type', 'description', 'requester_level'].includes(key)))
      setSettings(filteredSettings)
    } catch {
      setSettings({})
    } finally {
      setIsSettingDataLoading(false)
    }
  }, [accountId, dataProvider, notify])

  useEffect(() => {
    if (open) {
      fetchData()
      fetchAccSettings()
    }
  }, [open, fetchData, fetchAccSettings])

  const handleSearchInputChange = useCallback((inputValue: string) => {
    setSearchQuery(inputValue)
  }, [])

  const stateOptions = useMemo(
    () => [
      { value: USER_STATE.ACTIVE, label: 'Active Team Members of ' + accountName },
      { value: USER_STATE.ARCHIVED, label: 'Archived Team Members of ' + accountName },
    ],
    []
  )

  const filteredData = useMemo(() => {
    const list = (userData ?? []).filter((u) =>
      stateFilter === USER_STATE.ACTIVE ? ACTIVE_USER_STATES.includes(u.state) : stateFilter === USER_STATE.ARCHIVED ? u.state === USER_STATE.ARCHIVED : true
    )

    const q = searchQuery.trim().toLowerCase()
    if (!q) return list

    return list.filter((u) => {
      const fullName = (u.fullName ?? '').toLowerCase()
      const firstName = (u.firstName ?? '').toLowerCase()
      const lastName = (u.lastName ?? '').toLowerCase()
      const email = (u.primaryEmail ?? '').toLowerCase()
      return fullName.includes(q) || firstName.includes(q) || lastName.includes(q) || email.includes(q)
    })
  }, [userData, stateFilter, searchQuery])



  const reset2FAdevice = async (userId: string) => {
    if (processing2FAUserId) {
      notify('Another user is being processed. Please wait.', { type: 'warning' })
      return
    }
    // Set the processing state to show loading for this specific user
    setProcessing2FAUserId(userId)
    try {
      await dataProvider.update('Account', {
        id: userId,
        data: {
          action: 'reset_2fa_device',
          data: { user_id: userId, account_id: accountId },
        },
        previousData: null,
      })

      notify('Any 2FA device present in our system will be cleared. The user may register a new device during the next sign-in.', { type: 'success' })
    } catch (error) {
      const errorMessage = (error as { message?: string }).message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
    } finally {
      setProcessing2FAUserId(null)
    }
  }

  const openCreateUserModal = useCallback(() => {
    showModal(
      <CreateUser
        accountId={accountId}
        accountName={accountName}
        onClosed={(reason) => {
          if (reason === 'created') {
            fetchData()
          }
        }}
      />
    )
  }, [accountId, accountName, fetchData, showModal])

  const pageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus the page when navigated
    pageRef.current?.focus()
  }, [])

  const optionalCols =  (show2FAReset ? 1 : 0) + (canEditGlobalUser ||  canEditUser ? 1 : 0) 
  // base columns are: User, Title, Role, State, Info  => 5
  const gridCols = 5 + optionalCols

  return (
    <>
      <div className={styles.userTableNavigation} aria-label='Team Members List' tabIndex={-1} ref={pageRef} style={{ outline: 'none' }}>
        <div className={sharedStyles.tableInfo}>
          <div>
            <LxIcon sxStyles={{ height: '80px', width: '80px', marginTop: '-18px' }} icon={TeamMembersIcon}></LxIcon>
          </div>
          <div>
            <LxAutocompleteTmp value={stateFilter} onChange={setStateFilter} options={stateOptions} ariaLabel='Drop Down to select Active, Archived team members' />
            {/* {stateFilter == 'deleted' && <span>Deleted learner profiles can be restored within 90 days</span>} */}
          </div>
        </div>
        <div className={sharedStyles.tableActions}>
          {(canEditGlobalUser || canEditUser) ? (
            <div
              className={cn('lxActionButton lxActionButtonFilled lxActionButtonDefaultSize')}
              onClick={() => {
                openCreateUserModal()
              }}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openCreateUserModal()
                }
              }}
              aria-label={`Add New Team Member to ${accountName}`}
            >
              <LxIcon icon={PlusIcon} />
              Add New Team Member
            </div>
          ) : null}

          <div className={cn('lxActionButton', 'lxActionButtonDefaultSize', 'inputHighLight')}>
            <LxIcon icon={SearchIcon} />
            <input
              className={'pristineInput'}
              type='text'
              onChange={(e) => {
                handleSearchInputChange(e.target.value)
              }}
              placeholder='Search'
              aria-label='Search Team members'
            />
          </div>
        </div>
      </div>
      <div className={cn(styles[`teamMembersListContent-${gridCols}`])}>
        <div className={cn(sharedStyles.tableListHeader)}> User</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide2column)}> Title</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide3column)}> Role</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide3column)}> State</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide4column)}> Info</div>
      

        {show2FAReset && (
          <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide6column, styles.headerAction)}>
            Reset 2FA
            <LxTooltip tooltipText={"Reset the user's 2FA device if they cannot access it. The user will be prompted to register a new device at their next sign-in."}>
              <LxIcon icon={InfoOutlineIcon} />
            </LxTooltip>
          </div>
        )}
        {(canEditGlobalUser ||  canEditUser) && <div className={cn(sharedStyles.tableListHeader)}>Actions</div>}

        {isUserDataLoading || isSettingDataLoading ? (
          <LxLoadingSpinner className={sharedStyles.loader}></LxLoadingSpinner>
        ) : (
          <>
            {filteredData?.length ? (
              filteredData.map((user: UserF, index) => (
                <React.Fragment key={`User-row-${user.id}`}>
                  <div>
                    <span className={sharedStyles.textOverflow}>{user.fullName}</span>
                    {user.state !== USER_STATE.ACTIVE && (
                      <LxTooltip tooltipText={getTooltipText(user.state)}>
                        {user.state === USER_STATE.INVITE_FAILED ? (
                          <WarningAmberIcon className={styles.failedInviteRow} sx={{ height: '24px', width: '24px', marginLeft: 1 }} />
                        ) : [USER_STATE.INVITED, USER_STATE.INVITE_DELIVERED].includes(user.state) ? (
                          <LxIcon icon={InfoOutlineIcon} sxStyles={{ height: '24px', width: '24px', marginLeft: 1 }} />
                        ) : null}
                      </LxTooltip>
                    )}
                  </div>
                  <div className={cn(sharedStyles.hide2column, sharedStyles.noCursor)}>{user.jobTitle}</div>
                  <div className={cn(sharedStyles.hide3column, sharedStyles.noCursor)}>{mapRoleToReadableRole(user.userRole)}</div>
                  <div className={cn(sharedStyles.hide4column, sharedStyles.noCursor)}>{mapStateToReadableStatus(user.state)}</div>

                  <div className={sharedStyles.hide4column}>
                    <LxClickAbleIcon
                      onClick={() => showModal(<UserInfo user={user} onUpdated={fetchData} accountId={accountId!} accountName={accountName!} canEditUser={canEditGlobalUser ||  canEditUser} />)}
                      icon={UserInfoIcon}
                      ariaLabel={`Learner Info for ${user.fullName}`}
                      role='button'
                    ></LxClickAbleIcon>
                  </div>

                  

                  {show2FAReset && (userRolesWith2FA === 'all_users' || userRolesWith2FA === 'only_admins') && (
                    <div className={sharedStyles.hide4column}>
                      {userRolesWith2FA === 'all_users' || (userRolesWith2FA === 'only_admins' && ADMIN_ROLES.includes(user.userRole)) ? (
                        processing2FAUserId === user.id ? (
                          <div style={customStyles.smallSpinner}>
                            <CircularProgress size={20} />
                          </div>
                        ) : (
                          <LxSaveButton
                            onSubmit={() => reset2FAdevice(user.id)}
                            classNames='lxActionButton,lxActionButtonFilled'
                            label='Reset'
                            ariaLabel={`Reset 2FA Device for ${user.firstName} ${user.lastName}`}
                          />
                        )
                      ) : (
                        <LxTooltip tooltipText='2FA is not enabled for this user based on account settings'>
                          <LxIcon icon={MinusIcon} sxStyles={{ height: '24px', width: '24px', marginLeft: 1 }} />
                        </LxTooltip>
                      )}
                    </div>
                  )}

                  {(canEditGlobalUser ||  canEditUser) && (
                    <div className={cn(sharedStyles.hide6column, styles.noHoverCell)}>
                      <UserActions
                        user={user}
                        userList={filteredData}
                        accountId={accountId!}
                        accountName={accountName!}
                        canEditUser={canEditGlobalUser ||  canEditUser}
                        currentUser={currentUser}
                        isLastChild={index === filteredData.length - 1}
                        hasLockFunction={hasLockFunction}
                        onUpdated={fetchData}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className={sharedStyles.notFound}>No users match your current search or filters.</div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default AccountUsers
